import { FC, ReactNode } from 'react'
import { Crown } from 'lucide-react'
import { useSubscriptionTooling } from '../../hooks/use-subscription'
import { SubscriptionTypeEnum } from '../../lib/enums'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { useNavigate } from 'react-router-dom'

type SubscriptionGateProps = {
  children: ReactNode
  requiredTier?: SubscriptionTypeEnum
  fallback?: ReactNode
  mode?: 'hide' | 'disable' | 'replace'
  upgradeMessage?: string
}

/**
 * SubscriptionGate
 *
 * Wraps children and controls access based on subscription tier.
 *
 * Modes:
 * - 'hide': Hides the children completely if user doesn't have access
 * - 'disable': Shows children but disables interaction (default for buttons)
 * - 'replace': Shows a custom fallback component instead
 *
 * @example
 * <SubscriptionGate requiredTier={SubscriptionTypeEnum.Premium} mode="disable">
 *   <Button onClick={createListing}>Create Listing</Button>
 * </SubscriptionGate>
 */
const SubscriptionGate: FC<SubscriptionGateProps> = ({
  children,
  requiredTier = SubscriptionTypeEnum.Premium,
  fallback,
  mode = 'disable',
  upgradeMessage = 'Upgrade to access this feature'
}) => {
  const { hasAccess } = useSubscriptionTooling()
  const navigate = useNavigate()
  const hasRequiredAccess = hasAccess(requiredTier)

  // User has access - render normally
  if (hasRequiredAccess) {
    return <>{children}</>
  }

  // User doesn't have access - apply restrictions
  switch (mode) {
    case 'hide':
      return null

    case 'replace':
      return <>{fallback}</>

    case 'disable':
    default:
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative inline-block">
                {/* Render children with pointer-events disabled */}
                <div className="pointer-events-none opacity-50">
                  {children}
                </div>

                {/* Overlay with upgrade click */}
                <div
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => navigate('/settings?tab=subscription')}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <div className="flex items-start gap-2">
                <Crown className="w-4 h-4 text-sumoPrimary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">{upgradeMessage}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Click to view upgrade options
                  </p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
  }
}

export default SubscriptionGate
